import { Component } from '@headless-workspace/typings';
import { GlowIcon, GlowTextBody, Icons, PropsWithFontFamily } from '../../../../src';
import { GlowCTA } from '../cta/GlowCTA';

type GlowProductRatingProps = PropsWithFontFamily & {
    rating: number;
    reviewCount: number;
    ariaLabel: string;
    reviewsText: string;
    noReviewsText: string;
    onClick: () => void;
    structuredData?: {
        ratingItemProp?: string;
        reviewItemProp?: string;
    };
};

const MAX_RATING = 5;

/**
 * @param rating - The rating value to display.
 * @param reviewCount - The number of reviews.
 * @param ariaLabel - The aria label for the rating.
 * @param reviewsText - The text to display for reviews.
 * @param noReviewsText - The text to display when there are no reviews.
 * @param fontFamily - The font family to use for the text. Can be 'brand' or 'arial'.
 * @param onClick - The function to call when the rating is clicked.
 * @param structuredData - Optional structured data for SEO.
 */
export const GlowProductRating: Component<GlowProductRatingProps> = ({
    rating,
    reviewCount,
    ariaLabel,
    reviewsText,
    noReviewsText,
    fontFamily,
    onClick,
    structuredData,
}) => {
    if (reviewCount <= 0) {
        return <GlowTextBody fontFamily={fontFamily} fontWeight={'medium'} text={noReviewsText} />;
    }

    return (
        <GlowCTA className={'flex gap-0.5'} onClick={onClick} ariaLabel={ariaLabel}>
            <span className={'flex'}>
                <GlowIcon Icon={Icons.Star} type={'medium'} />
                <GlowTextBody
                    fontFamily={fontFamily}
                    fontWeight={'bold'}
                    text={rating}
                    TagName={'span'}
                    structuredData={{
                        itemProp: structuredData?.ratingItemProp,
                    }}
                />
                <GlowTextBody fontFamily={fontFamily} fontWeight={'medium'} text={`/${MAX_RATING}`} TagName={'span'} />
            </span>
            {structuredData?.reviewItemProp && (
                <meta itemProp={structuredData.reviewItemProp} content={reviewCount.toString()} />
            )}
            <GlowTextBody
                fontWeight={'medium'}
                fontFamily={fontFamily}
                textDecoration={'underline'}
                text={`(${reviewCount} ${reviewsText})`}
                TagName={'span'}
            />
        </GlowCTA>
    );
};
